import React, { useState, useRef, useEffect, useCallback } from 'react';
import Icon from '../ui/Icon';
import NotificationsDropdown from './NotificationsDropdown';
import ProfileDropdown from './ProfileDropdown';
import { dashboardAPI, ticketsAPI } from '../services/api';
import { formatInTz } from '../utils/timezone';
import { toast } from '../utils/toast';

// ── Notification sound (Web Audio API — no external files) ────────────────
function playNotifSound(type = 'notification') {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const play = (freq, t, dur, vol = 0.2) => {
      const osc = ctx.createOscillator();
      const g   = ctx.createGain();
      osc.connect(g); g.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, t);
      g.gain.setValueAtTime(vol, t);
      g.gain.exponentialRampToValueAtTime(0.001, t + dur);
      osc.start(t); osc.stop(t + dur);
    };
    const now = ctx.currentTime;
    if (type === 'new_ticket') {
      play(880, now, 0.25); play(1100, now + 0.2, 0.35);
    } else {
      play(660, now, 0.3);
    }
    setTimeout(() => { try { ctx.close(); } catch {} }, 1200);
  } catch { /* ignore */ }
}

export default function Topbar({ 
  isMobile, 
  setSidebarOpen, 
  topbarTitle, 
  onTicketsPage, 
  userProfile,
  onOpenSettings,
  onOpenOutOfOffice,
  onOpenAppearance,
  onLogout
}) {
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showPauseRequests, setShowPauseRequests] = useState(false);
  const [pauseRequests, setPauseRequests] = useState([]);
  const [pauseReqLoading, setPauseReqLoading] = useState(false);
  const notificationsRef = useRef(null);
  const profileRef = useRef(null);
  const pauseRef = useRef(null);
  const prevUnreadRef = useRef(0); // track previous unread count to detect new ones

  const isManager = ['admin','manager','ceo','supervisor','lead'].includes(userProfile?.role);

  const refreshPauseRequests = useCallback(() => {
    if (!isManager) return;
    ticketsAPI.getPendingPauseRequests()
      .then(rows => setPauseRequests(rows || []))
      .catch(() => {});
  }, [isManager]);

  useEffect(() => {
    refreshPauseRequests();
    const id = setInterval(refreshPauseRequests, 30000);
    return () => clearInterval(id);
  }, [refreshPauseRequests]);

  const handlePauseAction = useCallback(async (reqId, action) => {
    setPauseReqLoading(true);
    try {
      if (action === 'approve') {
        await ticketsAPI.approvePauseRequest(reqId);
        window.dispatchEvent(new CustomEvent('sysnet-pause-approved'));
      } else {
        await ticketsAPI.rejectPauseRequest(reqId);
        window.dispatchEvent(new CustomEvent('sysnet-pause-rejected'));
      }
      refreshPauseRequests();
    } catch(e) { toast(e.message); }
    setPauseReqLoading(false);
  }, [refreshPauseRequests]);

  const refreshUnreadCount = useCallback(() => {
    dashboardAPI.notifications()
      .then((rows) => {
        const count = (rows || []).filter(n => !n.is_read).length;
        // Play sound when unread count increases (new notification arrived)
        if (prevUnreadRef.current !== undefined && count > prevUnreadRef.current) {
          playNotifSound('notification');
        }
        prevUnreadRef.current = count;
        setUnreadCount(count);
      })
      .catch(() => setUnreadCount(0));
  }, []);

  useEffect(() => {
    refreshUnreadCount();
    const id = setInterval(refreshUnreadCount, 30000); // poll every 30 s
    return () => clearInterval(id);
  }, [refreshUnreadCount]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setShowProfile(false);
      }
      if (pauseRef.current && !pauseRef.current.contains(event.target)) {
        setShowPauseRequests(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div style={{ background: 'var(--topbar-bg)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', borderBottom: '1px solid var(--topbar-border)', height: 50, padding: '0 20px', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0, position: 'relative', zIndex: 40 }}>
      {isMobile && (
        <button onClick={() => setSidebarOpen(true)} style={{ padding: 4, background: 'transparent', border: 'none', borderRadius: 6, cursor: 'pointer' }}>
          <Icon name="grid" size={20} color="var(--accent)" />
        </button>
      )}

      <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>{topbarTitle}</span>
      <div style={{ flex: 1 }} />

      {!onTicketsPage && !isMobile && (
        <div style={{ height: 32, background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '0 12px', fontSize: 12, color: 'var(--muted)', display: 'flex', alignItems: 'center', gap: 6, width: 200, cursor: 'text' }}>
          <Icon name="search" size={13} color="#718096" /> Search
        </div>
      )}

      {/* Timer Pause Requests (managers only) */}
      {isManager && (
        <div style={{ position: 'relative' }} ref={pauseRef}>
          <button
            onClick={() => { setShowPauseRequests(!showPauseRequests); setShowNotifications(false); setShowProfile(false); }}
            title="Timer Pause Requests"
            style={{
              padding: '5px 12px', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer',
              background: pauseRequests.length > 0 ? '#FEF3C7' : 'transparent',
              border: pauseRequests.length > 0 ? '1.5px solid #F59E0B' : '1.5px solid #E2E8F0',
              borderRadius: 8, color: pauseRequests.length > 0 ? '#92400E' : '#718096',
              fontWeight: 600, fontSize: 12, position: 'relative', transition: 'all 0.2s',
            }}
          >
            <span style={{ fontSize: 14 }}>⏸</span>
            <span>Pause Requests</span>
            {pauseRequests.length > 0 && (
              <span style={{
                minWidth: 18, height: 18, padding: '0 5px',
                background: '#F59E0B', borderRadius: 99,
                fontSize: 10, fontWeight: 800, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {pauseRequests.length > 9 ? '9+' : pauseRequests.length}
              </span>
            )}
          </button>
          {showPauseRequests && (
            <div style={{ position: 'absolute', top: '110%', right: 0, width: 420, maxWidth: '95vw', background: '#fff', borderRadius: 14, boxShadow: '0 12px 40px rgba(0,0,0,0.15)', border: '1px solid #E2E8F0', zIndex: 1000, overflow: 'hidden' }}>
              <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)', fontWeight: 700, fontSize: 14, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span>⏸</span> Timer Pause Requests
                {pauseRequests.length > 0 && <span style={{ marginLeft: 'auto', background: '#FEF3C7', color: '#92400E', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 99 }}>{pauseRequests.length} pending</span>}
              </div>
              <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                {pauseRequests.length === 0 ? (
                  <div style={{ padding: '24px 16px', textAlign: 'center', color: '#94A3B8', fontSize: 13 }}>No pending pause requests</div>
                ) : pauseRequests.map(r => (
                  <div key={r.id} style={{ padding: '12px 16px', borderBottom: '1px solid #F3F4F6' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 8 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text)' }}>
                          {r.requested_by_name} — <span style={{ color: 'var(--accent)' }}>{r.ticket_number}</span>
                        </div>
                        <div style={{ fontSize: 12, color: '#64748B', marginTop: 2 }}>{r.subject}</div>
                        <div style={{ fontSize: 12, color: '#374151', marginTop: 6, background: '#FFF7ED', border: '1px solid #FDE68A', borderRadius: 6, padding: '5px 9px' }}>
                          "{r.remark}"
                        </div>
                        <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 4 }}>
                          {formatInTz(r.created_at)}
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        onClick={() => handlePauseAction(r.id, 'approve')}
                        disabled={pauseReqLoading}
                        style={{ flex: 1, padding: '7px 0', border: 'none', borderRadius: 7, background: '#10B981', color: '#fff', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}
                      >
                        ✓ Approve
                      </button>
                      <button
                        onClick={() => handlePauseAction(r.id, 'reject')}
                        disabled={pauseReqLoading}
                        style={{ flex: 1, padding: '7px 0', border: '1px solid #E2E8F0', borderRadius: 7, background: '#fff', color: '#EF4444', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}
                      >
                        ✕ Reject
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Notifications */}
      <div style={{ position: 'relative' }} ref={notificationsRef}>
        <button
          onClick={() => { setShowNotifications(!showNotifications); setShowProfile(false); }}
          style={{ padding: 6, color: '#718096', background: 'transparent', border: 'none', borderRadius: 6, cursor: 'pointer', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <Icon name="bell" size={20} color="#718096" />
          {unreadCount > 0 && (
            <span style={{
              position: 'absolute', top: 2, right: 2, minWidth: 16, height: 16, padding: '0 4px',
              background: '#ef4444', border: '2px solid #FFFFFF', borderRadius: 99,
              fontSize: 9, fontWeight: 800, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
        {showNotifications && (
          <NotificationsDropdown
            onClose={() => setShowNotifications(false)}
            onUnreadChange={setUnreadCount}
          />
        )}
      </div>

      {/* Profile */}
      <div style={{ position: 'relative' }} ref={profileRef}>
        <button
          onClick={() => { setShowProfile(!showProfile); setShowNotifications(false); }}
          style={{ width: 30, height: 30, borderRadius: '50%', background: 'var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: 'var(--accent)', fontSize: 12, overflow: 'hidden', border: '1.5px solid var(--border2)', cursor: 'pointer', marginLeft: 4 }}
        >
          {userProfile.avatar
            ? <img src={userProfile.avatar} alt="Avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : userProfile.name.charAt(0)
          }
        </button>
        {showProfile && (
          <ProfileDropdown
            onClose={() => setShowProfile(false)}
            userProfile={userProfile}
            onOpenSettings={() => { onOpenSettings(); setShowProfile(false); }}
            onOpenOutOfOffice={() => { onOpenOutOfOffice(); setShowProfile(false); }}
            onOpenAppearance={() => { onOpenAppearance(); setShowProfile(false); }}
            onLogout={onLogout}
          />
        )}
      </div>
    </div>
  );
}
